import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { Building2, Mail, Plus, X } from 'lucide-react'
import { api } from '@/services/api'

export interface EditClientEmailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  isLoading?: boolean
  client: { id: string; name: string; email: string } | null
}

interface ClientUser {
  id: string
  nom: string
  email: string
}

export function EditClientEmailModal({
  open,
  onOpenChange,
  onSuccess,
  isLoading: externalLoading = false,
  client,
}: EditClientEmailModalProps) {
  const [clientNom, setClientNom] = useState('')
  const [emails, setEmails] = useState<string[]>([''])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Fetch all emails for this client when modal opens
  useEffect(() => {
    if (open && client?.name) {
      setClientNom(client.name)
      fetchClientEmails(client.name)
    }
  }, [open, client?.name, client?.id])

  const fetchClientEmails = async (clientName: string) => {
    if (!clientName) return
    setLoading(true)
    try {
      const response = await api.get<{ data: ClientUser[]; count: number }>(
        `/users/client/${encodeURIComponent(clientName)}/emails`
      )
      const clientEmails = response.data.data?.map(u => u.email) || []
      setEmails(clientEmails.length > 0 ? clientEmails : [client?.email || ''])
    } catch (error) {
      console.error('Erreur lors de la récupération des emails:', error)
      // Fallback to the single email from props
      setEmails([client?.email || ''])
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails]
    newEmails[index] = value
    setEmails(newEmails)
  }

  const addEmailField = () => {
    setEmails([...emails, ''])
  }

  const removeEmailField = (index: number) => {
    // Ensure at least 1 valid email remains
    const validEmailsAfterRemove = emails.filter((e, i) => i !== index && e && e.trim() && isValidEmail(e.trim()))
    if (validEmailsAfterRemove.length >= 1 || emails.length > 1) {
      const newEmails = [...emails]
      newEmails.splice(index, 1)
      setEmails(newEmails)
    }
  }

  // Check if we can remove this email (at least 1 valid email must remain)
  const canRemoveEmail = (index: number) => {
    if (emails.length <= 1) return false
    const validEmailsAfterRemove = emails.filter((e, i) => i !== index && e && e.trim() && isValidEmail(e.trim()))
    return validEmailsAfterRemove.length >= 1
  }

  const handleSubmit = async () => {
    if (!client?.id) return

    const nom = clientNom.trim()
    if (!nom) {
      alert('Veuillez entrer un nom de client')
      return
    }

    const validEmails = emails.filter(e => e && e.trim() && isValidEmail(e.trim()))
    if (validEmails.length === 0) {
      alert('Veuillez entrer au moins un email valide')
      return
    }

    setSaving(true)
    try {
      if (nom !== (client.name || '').trim()) {
        await api.put(`/users/clients/${client.id}`, { nom })
      }
      await api.put(`/users/client/${encodeURIComponent(nom)}/emails`, {
        emails: validEmails.map(e => e.trim()),
      })
      onSuccess()
      onOpenChange(false)
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour des emails:', error)
      const errorMessage = error?.response?.data?.detail || 'Erreur lors de la mise à jour des emails'
      alert(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const isLoading = externalLoading || loading || saving

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={loading || !clientNom.trim() || emails.filter(e => e && e.trim()).length === 0}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Modifier le client</h2>
            <p className="text-sm text-gray-500 mt-1">
              Nom de l&apos;agence et adresses email de connexion
            </p>
          </div>
        </div>

        {loading ? (
          <PageSkeleton variant="rows" />
        ) : (
          <>
          <FormField label="Nom du client" required>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                value={clientNom}
                onChange={(e) => setClientNom(e.target.value)}
                placeholder="Nom de l'agence"
                className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
              />
            </div>
          </FormField>

          <FormField label="Adresses email">
            <div className="space-y-2">
              {emails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      placeholder="contact@exemple.com"
                      className={`bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10 ${
                        email && !isValidEmail(email) ? 'border-red-300' : ''
                      }`}
                    />
                  </div>
                  {canRemoveEmail(index) && (
                    <button
                      type="button"
                      onClick={() => removeEmailField(index)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer cet email"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addEmailField}
                className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 mt-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter un email
              </button>
              <p className="text-xs text-gray-500 mt-2">
                💡 Chaque email pourra se connecter à la plateforme et accéder aux projets du client.
              </p>
            </div>
          </FormField>
          </>
        )}

        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Attention :</strong> Supprimer un email supprimera le compte utilisateur associé.
          </p>
        </div>
      </div>
    </Modal>
  )
}
